import {
  Button,
  capitalize,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { couplerTypeToLocaleString } from "common/utils/enumTranslations";
import { Field, FieldArray, FieldProps, useFormikContext } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormikTextInput } from "common/components/FormikTextInput";
import { getInputGuid } from "portal/utils/ids";
import { HydraulicCouplerType } from "protos/atk/enums";
import { ImplementArchetype, ScvInput } from "protos/atk/autotractor";
import { ImplementViewModel } from "../ImplementViewModel";
import { TemplateOverride } from "../../TemplateOverride";
import { typedPath } from "typed-path";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { FC, useState } from "react";

const S = typedPath<ImplementViewModel>();

export const ScvStep: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const formikProps = useFormikContext<ImplementViewModel>();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    Boolean(formikProps.values.scvInputs)
  );

  const hasCustomInfo =
    Boolean(formikProps.values.scvInputs) || checkedOverrideBox;

  return (
    <div className="flex flex-col gap-2">
      <TemplateOverride
        checked={hasCustomInfo}
        disabled={disabled}
        onChange={() => {
          if (hasCustomInfo) {
            setCheckedOverrideBox(false);
            formikProps.setFieldValue(S.scvInputs.$path, undefined);
          } else {
            setCheckedOverrideBox(true);
            formikProps.setFieldValue(
              S.scvInputs.$path,
              formikProps.values.archetype?.scvInputs
            );
          }
        }}
      />
      {hasCustomInfo && (
        <Paper className="flex flex-col gap-2 p-4">
          <FieldArray
            name={S.scvInputs.inputs.$path}
            render={(arrayHelpers) => (
              <div className="flex flex-col gap-2">
                <Typography>{t("models.equipment.scvInput_other")}</Typography>
                {formikProps.values.scvInputs?.inputs.map((s, i) => (
                  <div
                    key={`scvInput-${i}`}
                    className="flex flex-col gap-2 p-4 rounded-xl bg-darken-200"
                  >
                    <div className="flex justify-between">
                      <div className="flex gap-2">
                        <FormikTextInput
                          name={`${S.scvInputs.inputs.$path}[${i}].function`}
                          label={capitalize(t("models.equipment.function"))}
                          tooltipText={t(
                            "views.autotractor.equipment.implements.functionTooltip"
                          )}
                          disabled={disabled}
                        />
                      </div>
                      <IconButton
                        color="inherit"
                        className="flex my-auto"
                        onClick={() => arrayHelpers.remove(i)}
                        disabled={disabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                    {/* todo: tooltip, recommended TRACTOR port */}
                    <FormikTextInput
                      name={`${S.scvInputs.inputs.$path}[${i}].recommendedPort`}
                      label={t(
                        "views.autotractor.equipment.implements.recommendedTractorPort"
                      )}
                      tooltipText={t(
                        "views.autotractor.equipment.implements.recommendedTractorPortTooltip"
                      )}
                      disabled={disabled}
                    />
                    <div className="flex flex-col">
                      <Typography>
                        {capitalize(t("models.equipment.couplerDiameter"))}
                      </Typography>
                      <Field name={`${S.scvInputs.inputs.$path}[${i}].coupler`}>
                        {({
                          field,
                          meta,
                        }: FieldProps<number, ImplementArchetype>) => (
                          <Select
                            {...field}
                            value={s.coupler}
                            autoWidth
                            size="small"
                            error={Boolean(meta.error)}
                            disabled={disabled}
                          >
                            <MenuItem
                              value={
                                HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                              }
                            >
                              {couplerTypeToLocaleString(
                                t,
                                HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                              )}
                            </MenuItem>
                            <MenuItem
                              value={
                                HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                              }
                            >
                              {couplerTypeToLocaleString(
                                t,
                                HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                              )}
                            </MenuItem>
                          </Select>
                        )}
                      </Field>
                    </div>
                  </div>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() =>
                    arrayHelpers.push(
                      ScvInput.fromPartial({ id: getInputGuid() })
                    )
                  }
                  disabled={disabled}
                >
                  {t("views.autotractor.equipment.tractors.addScv")}
                </Button>
              </div>
            )}
          />
        </Paper>
      )}
      {!hasCustomInfo && (
        <Paper className="flex flex-col gap-2 p-4">
          <FieldArray
            name={S.archetype.scvInputs.inputs.$path}
            render={() => (
              <div className="flex flex-col gap-2">
                <Typography>{t("models.equipment.scvInput_other")}</Typography>
                {formikProps.values.archetype?.scvInputs?.inputs.map((s, i) => (
                  <div
                    key={`scvInput-${i}`}
                    className="flex flex-col gap-2 p-4 rounded-xl bg-darken-200"
                  >
                    <div className="flex justify-between">
                      <div className="flex gap-2">
                        <FormikTextInput
                          disabled
                          name={`${S.archetype.scvInputs.inputs.$path}[${i}].function`}
                          label={capitalize(t("models.equipment.function"))}
                          tooltipText={t(
                            "views.autotractor.equipment.implements.functionTooltip"
                          )}
                        />
                      </div>
                    </div>
                    <FormikTextInput
                      disabled
                      name={`${S.archetype.scvInputs.inputs.$path}[${i}].recommendedPort`}
                      label={t(
                        "views.autotractor.equipment.implements.recommendedTractorPort"
                      )}
                      tooltipText={t(
                        "views.autotractor.equipment.implements.recommendedTractorPortTooltip"
                      )}
                    />
                    <FormikDimensionedNumberInput
                      name={`${S.archetype.scvInputs.inputs.$path}[${i}].couplerDiameterMm`}
                      label={capitalize(t("models.equipment.couplerDiameter"))}
                      dimensionedNumberInputProps={{
                        disabled: true,
                        storedUnit: "mm",
                        displayUnits: { imperial: "in", metric: "mm" },
                        localSystem: measurementSystem,
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        </Paper>
      )}
    </div>
  );
};
